import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { StepperRefAttributes } from 'primereact/stepper'
import { Steps } from 'primereact/steps'
import { classNames } from 'primereact/utils'

import {
  EInvoiceDto,
  EinvoiceValidationresult,
} from '../../../../../../data/models'
import { EInvoiceValidator } from '../../../../../../data/models/EInvoice'
import { useGeneralSettings } from '../../../../../selfservice/settings/data'
import {
  DetailsDialog,
  ResultPanel,
  RuleSetsInfoDialog,
} from '../../../../common'
import {
  EInvoiceError as EInvoiceErrorType,
  ExtendedEInvoiceValidationResult,
} from '../../../../data'
import { Footer } from '../footer'
import {
  getInvoiceFormatFromEInvoice,
  getInvoiceFormatFromError,
} from './utils'

enum ValidationStep {
  VIRUS = 0,
  FORMAT = 1,
  CONFORMANCE = 2,
  DUPLICATE = 3,
  MASTER_DATA = 4,
  RENDER_PREVIEW = 5,
}

interface StepStatus {
  status: 'error' | 'warning' | 'success'
  isDisabled: boolean
}

interface ValidationResults {
  stepStatuses: Record<ValidationStep, StepStatus>
  virusErrors: ExtendedEInvoiceValidationResult[]
  formatErrors: ExtendedEInvoiceValidationResult[]
  formatWarnings: ExtendedEInvoiceValidationResult[]
  conformanceErrors: ExtendedEInvoiceValidationResult[]
  conformanceWarnings: ExtendedEInvoiceValidationResult[]
  duplicateErrors: ExtendedEInvoiceValidationResult[]
  companyCodeErrors: ExtendedEInvoiceValidationResult[]
  companyCodeWarnings: ExtendedEInvoiceValidationResult[]
  companyCodeSuccess: ExtendedEInvoiceValidationResult[]
  allErrors: ExtendedEInvoiceValidationResult[]
  allWarnings: ExtendedEInvoiceValidationResult[]
}

type Props = {
  eInvoice?: EInvoiceDto
  errors?: EInvoiceErrorType
  stepperRef: React.MutableRefObject<StepperRefAttributes | null>
}
export const Validation: React.FC<Props> = ({
  eInvoice,
  errors,
  stepperRef,
}) => {
  const { t } = useTranslation('eInvoiceDetails')
  const [isDetailsVisible, setDetailsVisible] = useState(false)
  const [resultObject, setResultObject] =
    useState<EinvoiceValidationresult | null>(null)
  const [isRuleSetsInfoDialogVisible, setIsRuleSetsInfoDialogVisible] =
    useState(false)
  const { data: generalSettings, isLoading } = useGeneralSettings()

  const handleShowDetails = (result: EinvoiceValidationresult) => {
    setResultObject(result)
    setDetailsVisible(true)
  }

  // Choose where to get the errors from.
  // If you combine both sources, you get duplicates.
  // So if we have no errors, use the errors from eInvoice.
  const errorsSource = eInvoice?.ValidationResult?.Results
    ? eInvoice?.ValidationResult?.Results
    : errors?.Results || []
  const ruleSetsSource = eInvoice?.ValidationResult?.AvailableRuleSets
    ? eInvoice?.ValidationResult?.AvailableRuleSets
    : errors?.AvailableRulesets || []

  const hasCompanyCode = hasCompanyCodes(errorsSource)
  const hasError = hasErrors(errorsSource)
  const hasWarning = hasWarnings(errorsSource)

  const fileName = eInvoice?.Meta?.file.fileName || errors?.FileName
  const invoiceFormat =
    getInvoiceFormatFromEInvoice(eInvoice?.Meta?.file.subType) ||
    getInvoiceFormatFromError(errors?.InvoiceFormat) ||
    t('stepper.validation.card.header.error.description.undefinedFormat')
  const invoiceVersion =
    eInvoice?.Meta?.file?.version ||
    errors?.InvoiceVersion ||
    t('stepper.validation.card.header.error.description.undefinedVersion')

  // Calculate step statuses and validation logic
  const validationResults = getValidationResults(errorsSource)

  // Create dynamic step index mapping
  const getDisplayIndex = (validationStep: ValidationStep): number => {
    if (generalSettings?.isMalwareScanningActive) {
      // When virus scanning is active, use original enum values
      return validationStep
    } else {
      // When virus scanning is inactive, shift other steps down
      switch (validationStep) {
        case ValidationStep.FORMAT:
          return 0
        case ValidationStep.CONFORMANCE:
          return 1
        case ValidationStep.DUPLICATE:
          return 2
        case ValidationStep.MASTER_DATA:
          return 3
        case ValidationStep.RENDER_PREVIEW:
          return 4
        default:
          return validationStep
      }
    }
  }

  let steps = [
    {
      label: t('stepper.validation.card.steps.format.label'),
      template: (item: any) =>
        itemRenderer(
          item,
          getDisplayIndex(ValidationStep.FORMAT),
          validationResults.stepStatuses[ValidationStep.FORMAT],
          ValidationStep.FORMAT,
        ),
    },
    {
      label: t('stepper.validation.card.steps.conformance.label'),
      template: (item: any) =>
        itemRenderer(
          item,
          getDisplayIndex(ValidationStep.CONFORMANCE),
          validationResults.stepStatuses[ValidationStep.CONFORMANCE],
          ValidationStep.CONFORMANCE,
        ),
    },
    {
      label: t('stepper.validation.card.steps.duplicate.label'),
      template: (item: any) =>
        itemRenderer(
          item,
          getDisplayIndex(ValidationStep.DUPLICATE),
          validationResults.stepStatuses[ValidationStep.DUPLICATE],
          ValidationStep.DUPLICATE,
        ),
    },
    {
      label: t('stepper.validation.card.steps.masterData.label'),
      template: (item: any) =>
        itemRenderer(
          item,
          getDisplayIndex(ValidationStep.MASTER_DATA),
          validationResults.stepStatuses[ValidationStep.MASTER_DATA],
          ValidationStep.MASTER_DATA,
        ),
    },
    {
      label: t('stepper.validation.card.steps.renderPreview.label'),
      template: (item: any) =>
        itemRenderer(
          item,
          getDisplayIndex(ValidationStep.RENDER_PREVIEW),
          validationResults.stepStatuses[ValidationStep.RENDER_PREVIEW],
          ValidationStep.RENDER_PREVIEW,
        ),
    },
  ]

  if (generalSettings?.isMalwareScanningActive) {
    steps.unshift({
      label: t('stepper.validation.card.steps.virus.label'),
      template: (item: any) =>
        itemRenderer(
          item,
          getDisplayIndex(ValidationStep.VIRUS),
          validationResults.stepStatuses[ValidationStep.VIRUS],
          ValidationStep.VIRUS,
        ),
    })
  }

  const itemRenderer = (
    item: any,
    itemIndex: number,
    stepStatus: StepStatus,
    validationStep: ValidationStep,
  ) => {
    // Get the step icon based on status
    const getStepIcon = (status: 'error' | 'warning' | 'success'): string => {
      switch (status) {
        case 'error':
          return 'pi-times'
        case 'warning':
          return 'pi-exclamation-triangle'
        case 'success':
          return 'pi-check'
      }
    }

    const handleRuleSetsInfoClick = (e: React.MouseEvent) => {
      setIsRuleSetsInfoDialogVisible(true)
    }

    const showConformanceInfo =
      validationStep === ValidationStep.CONFORMANCE && !stepStatus.isDisabled
    const showMasterDataInfo =
      validationStep === ValidationStep.MASTER_DATA && !stepStatus.isDisabled

    return (
      <div className="flex flex-column align-items-center gap-2">
        <span
          className={classNames(
            'inline-flex align-items-center justify-content-center align-items-center border-circle h-2rem w-2rem z-1 text-white',
            {
              'bg-red-500': stepStatus.status === 'error',
              'bg-yellow-500': stepStatus.status === 'warning',
              'bg-green-500': stepStatus.status === 'success',
              'bg-gray-400': stepStatus.isDisabled,
            },
          )}
          style={{ marginTop: '-4px' }}
        >
          {stepStatus.isDisabled ? (
            itemIndex + 1
          ) : (
            <i className={`pi ${getStepIcon(stepStatus.status)} text-lg`} />
          )}
        </span>
        <div
          className={classNames('flex align-items-center', {
            'relative pr-5 ml-3':
              validationStep === ValidationStep.CONFORMANCE ||
              validationStep === ValidationStep.MASTER_DATA,
          })}
        >
          <span className="text-sm">{item.label}</span>
          {showConformanceInfo && (
            <Button
              aria-label={t(
                'stepper.validation.card.steps.conformance.info.label',
              )}
              icon="pi pi-info-circle"
              rounded
              text
              size="small"
              onClick={handleRuleSetsInfoClick}
              className="border-circle p-1 h-2rem w-2rem absolute right-0"
            />
          )}
          {/* {showMasterDataInfo && (
            <Button
              aria-label={t(
                'stepper.validation.card.steps.masterData.info.label',
              )}
              icon="pi pi-info-circle"
              rounded
              text
              size="small"
              onClick={handleRuleSetsInfoClick}
              className="border-circle p-1 h-2rem w-2rem absolute right-0"
            />
          )} */}
        </div>
      </div>
    )
  }

  const getHeader = (): JSX.Element => {
    let header = t('stepper.validation.card.header.success.title')
    if (hasWarning) header = t('stepper.validation.card.header.warning.title')
    if (hasError) header = t('stepper.validation.card.header.error.title')

    return (
      <h2
        className={classNames('text-xl text-green-700', {
          'text-yellow-700': hasWarning,
          'text-red-700': hasError,
        })}
      >
        {header}
      </h2>
    )
  }

  const getDescription = (): JSX.Element => {
    let description = t('stepper.validation.card.header.success.description', {
      invoiceFileName: fileName,
      invoiceType: invoiceFormat,
      invoiceVersion: invoiceVersion,
    })

    if (hasWarning)
      description = t('stepper.validation.card.header.warning.description', {
        invoiceFileName: fileName,
        invoiceType: invoiceFormat,
        invoiceVersion: invoiceVersion,
      })

    if (validationResults.stepStatuses[ValidationStep.VIRUS].status === 'error')
      description = t(
        'stepper.validation.card.header.error.description.virus',
        {
          invoiceFileName: fileName,
        },
      )

    if (
      validationResults.stepStatuses[ValidationStep.FORMAT].status === 'error'
    )
      description = t(
        'stepper.validation.card.header.error.description.format',
        {
          invoiceFileName: fileName,
          invoiceType: invoiceFormat,
          invoiceVersion: invoiceVersion,
        },
      )

    if (
      validationResults.stepStatuses[ValidationStep.CONFORMANCE].status ===
      'error'
    )
      description = t(
        'stepper.validation.card.header.error.description.conformance',
        {
          invoiceFileName: fileName,
          invoiceType: invoiceFormat,
          invoiceVersion: invoiceVersion,
        },
      )

    if (
      validationResults.stepStatuses[ValidationStep.DUPLICATE].status ===
      'error'
    )
      description = t(
        'stepper.validation.card.header.error.description.duplicate',
        {
          invoiceFileName: fileName,
        },
      )

    return (
      <p
        className={classNames('text-lg text-green-700', {
          'text-yellow-700': hasWarning,
          'text-red-700': hasError,
        })}
      >
        {description}
      </p>
    )
  }

  // Filter RuleSets for Conformance: validator === 0 (JoubiniValidator) + CompanyCodeRuleset
  const availableRuleSetsForJoubiniValidator = ruleSetsSource?.filter(rs => {
    if (rs.ruleset === 'CompanyCodeRuleset') return true
    return rs.validator === EInvoiceValidator.JoubiniValidator
  })

  const ruleSetNames = new Set(
    availableRuleSetsForJoubiniValidator?.map(rs => rs?.ruleset?.toLowerCase()),
  )

  const filteredResults = errorsSource?.filter(r => {
    return ruleSetNames.has(r.RuleSet?.toLowerCase() ?? '')
  })

  return (
    <>
      <RuleSetsInfoDialog
        title={t('stepper.validation.card.steps.conformance.info.dialog.title')}
        invoiceFormat={invoiceFormat}
        invoiceVersion={invoiceVersion}
        availableRuleSets={availableRuleSetsForJoubiniValidator}
        results={filteredResults}
        visible={isRuleSetsInfoDialogVisible}
        setVisible={setIsRuleSetsInfoDialogVisible}
      />

      <DetailsDialog
        resultObject={resultObject}
        visible={isDetailsVisible}
        setVisible={setDetailsVisible}
      />

      {/* Card */}
      <div className="border-round-lg shadow-1 overflow-hidden">
        {/* Header */}
        <div
          className={classNames('border-top-3	py-6', {
            'border-red-500 bg-red-50': hasError,
            'border-yellow-500 bg-yellow-50': hasWarning,
            'border-green-500 bg-green-50': !hasError && !hasWarning,
          })}
        >
          <div
            className="line-height-3 text-center mx-auto mb-6 pb-3"
            style={{ maxWidth: '600px' }}
          >
            {getHeader()}
            {getDescription()}
          </div>

          <Steps model={steps} readOnly={false} />
        </div>

        {/* Tables */}
        {(hasCompanyCode || hasError || hasWarning) && (
          <div
            className="p-3 md:pb-6"
            style={{
              backgroundImage: hasError
                ? 'linear-gradient(to bottom, var(--red-50) 0%, var(--surface-a) 120px)'
                : 'linear-gradient(to bottom, var(--yellow-50) 0%, var(--surface-a) 120px)',
            }}
          >
            {/* Found company code */}
            {hasCompanyCode && (
              <ResultPanel
                results={
                  [
                    ...validationResults.companyCodeErrors,
                    ...validationResults.companyCodeWarnings,
                    ...validationResults.companyCodeSuccess,
                  ] as EinvoiceValidationresult[]
                }
                status={
                  validationResults.companyCodeErrors.length > 0
                    ? 'Error'
                    : validationResults.companyCodeWarnings.length > 0
                    ? 'Warn'
                    : 'Success'
                }
                title={t(
                  'stepper.validation.card.content.foundCompanyCode.title',
                  {
                    count:
                      validationResults.companyCodeErrors.length +
                      validationResults.companyCodeWarnings.length +
                      validationResults.companyCodeSuccess.length,
                  },
                )}
                onInfoClick={handleShowDetails}
              />
            )}

            {/* All Errors */}
            {hasError && (
              <ResultPanel
                results={
                  validationResults.allErrors as EinvoiceValidationresult[]
                }
                status="Error"
                title={t('stepper.validation.card.content.errors.title', {
                  count: validationResults.allErrors.length,
                })}
                onInfoClick={handleShowDetails}
              />
            )}

            {/* All Warnings */}
            {hasWarning && (
              <ResultPanel
                results={
                  validationResults.allWarnings.sort((a, b) => {
                    const levelOrder: Record<string, number> = {
                      Warn: 0,
                      DisabledError: 1,
                    }
                    return (
                      (levelOrder[a.Level] ?? 2) - (levelOrder[b.Level] ?? 2)
                    )
                  }) as EinvoiceValidationresult[]
                }
                status="Warn"
                title={t('stepper.validation.card.content.warnings.title', {
                  count: validationResults.allWarnings.length,
                })}
                onInfoClick={handleShowDetails}
              />
            )}
          </div>
        )}
      </div>

      <Footer stepperRef={stepperRef} isFirstStep hasError={hasError} />
    </>
  )
}

const getValidationResults = (
  errors?: ExtendedEInvoiceValidationResult[],
): ValidationResults => {
  // Extract validation results by type
  // @ts-ignore
  const companyCodeResults = // @ts-ignore
    errors?.filter(error => error.ValidationType === 'CompanyCode') || []
  const companyCodeErrors = companyCodeResults.filter(
    error => error.Level === 'Error',
  )
  const companyCodeWarnings = companyCodeResults.filter(
    error => error.Level === 'Warn' || error.Level === 'DisabledError',
  )
  const companyCodeSuccess = companyCodeResults.filter(
    error => error.Level === 'Success',
  )

  const virusResults =
    errors?.filter(error => error.ValidationType === 'Virus') || []
  const virusErrors = virusResults.filter(error => error.Level === 'Error')

  const formatResults =
    errors?.filter(error => error.ValidationType === 'Format') || []
  const formatErrors = formatResults.filter(error => error.Level === 'Error')
  const formatWarnings = formatResults.filter(
    error => error.Level === 'Warn' || error.Level === 'DisabledError',
  )

  const conformanceResults =
    errors?.filter(error => error.ValidationType === 'Conformance') || []
  const conformanceErrors = conformanceResults.filter(
    error => error.Level === 'Error',
  )
  const conformanceWarnings = conformanceResults.filter(
    error => error.Level === 'Warn' || error.Level === 'DisabledError',
  )

  const duplicateResults =
    errors?.filter(error => error.ValidationType === 'Duplicate') || []
  const duplicateErrors = duplicateResults.filter(
    error => error.Level === 'Error',
  )

  // Determine step statuses
  const virusStatus: 'error' | 'warning' | 'success' =
    virusErrors.length > 0 ? 'error' : 'success'
  const formatStatus: 'error' | 'warning' | 'success' =
    formatErrors.length > 0
      ? 'error'
      : formatWarnings.length > 0
      ? 'warning'
      : 'success'
  const conformanceStatus: 'error' | 'warning' | 'success' =
    conformanceErrors.length > 0
      ? 'error'
      : conformanceWarnings.length > 0
      ? 'warning'
      : 'success'
  const duplicateStatus: 'error' | 'warning' | 'success' =
    duplicateErrors.length > 0 ? 'error' : 'success'
  const masterDataStatus: 'error' | 'warning' | 'success' =
    companyCodeErrors.length > 0
      ? 'error'
      : companyCodeWarnings.length > 0
      ? 'warning'
      : 'success'

  // Determine last validated step (where validation stopped due to error)
  let lastValidatedStep = ValidationStep.RENDER_PREVIEW // Default: all steps completed

  if (virusStatus === 'error') {
    lastValidatedStep = ValidationStep.VIRUS
  } else if (formatStatus === 'error') {
    lastValidatedStep = ValidationStep.FORMAT
  } else if (conformanceStatus === 'error') {
    lastValidatedStep = ValidationStep.CONFORMANCE
  } else if (duplicateStatus === 'error') {
    lastValidatedStep = ValidationStep.DUPLICATE
  }

  // Create step statuses
  const stepStatuses: Record<ValidationStep, StepStatus> = {
    [ValidationStep.VIRUS]: {
      status: virusStatus,
      isDisabled: lastValidatedStep < ValidationStep.VIRUS,
    },
    [ValidationStep.FORMAT]: {
      status: formatStatus,
      isDisabled: lastValidatedStep < ValidationStep.FORMAT,
    },
    [ValidationStep.CONFORMANCE]: {
      status: conformanceStatus,
      isDisabled: lastValidatedStep < ValidationStep.CONFORMANCE,
    },
    [ValidationStep.DUPLICATE]: {
      status: duplicateStatus,
      isDisabled: lastValidatedStep < ValidationStep.DUPLICATE,
    },
    [ValidationStep.MASTER_DATA]: {
      status: masterDataStatus,
      isDisabled: lastValidatedStep < ValidationStep.MASTER_DATA,
    },
    [ValidationStep.RENDER_PREVIEW]: {
      status: 'success',
      isDisabled: lastValidatedStep < ValidationStep.RENDER_PREVIEW,
    },
  }

  // Collect all errors and warnings
  const allErrors = [
    ...virusErrors,
    ...formatErrors,
    ...conformanceErrors,
    ...duplicateErrors,
  ]
  const allWarnings = [...formatWarnings, ...conformanceWarnings]

  return {
    stepStatuses,
    virusErrors,
    formatErrors,
    formatWarnings,
    conformanceErrors,
    conformanceWarnings,
    duplicateErrors,
    companyCodeErrors,
    companyCodeWarnings,
    companyCodeSuccess,
    allErrors,
    allWarnings,
  }
}

const hasCompanyCodes = (
  errors?: ExtendedEInvoiceValidationResult[],
): boolean => {
  // @ts-ignore
  return errors?.some(error => error.ValidationType === 'CompanyCode') ?? false
}

const hasWarnings = (errors?: ExtendedEInvoiceValidationResult[]): boolean => {
  return (
    errors?.some(
      error =>
        (isValidationTypeStep(error.ValidationType) &&
          error.Level === 'Warn') ||
        error.Level === 'DisabledError',
    ) ?? false
  )
}

const hasErrors = (errors?: ExtendedEInvoiceValidationResult[]): boolean => {
  return (
    errors?.some(
      error =>
        isValidationTypeStep(error.ValidationType) && error.Level === 'Error',
    ) ?? false
  )
}

/**
 * Checks if the validation type is one of the defined steps.
 * Used to filter relevant validations (Virus, Format, Conformance, Duplicate)
 * and exclude other custom validation types.
 *
 * @param validationType - The validation type to check
 * @returns true if the validation type is a defined step, false otherwise
 */
const isValidationTypeStep = (
  validationType: ExtendedEInvoiceValidationResult['ValidationType'],
): boolean => {
  switch (validationType) {
    case 'Virus':
    case 'Format':
    case 'Conformance':
    case 'Duplicate':
      return true
    default:
      return false
  }
}
